/**
 * Global Loading Bar Component
 * Displays a loading indicator at the top of the page during route transitions
 */

import { useNavigation } from 'react-router-dom';
import { useEffect, useState, memo } from 'react';

function GlobalLoadingBarComponent() {
  const navigation = useNavigation();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (navigation.state === 'loading') {
      // Start progress (deferred to next tick to avoid sync setState in effect)
      const timer0 = setTimeout(() => setProgress(20), 0);
      const timer1 = setTimeout(() => setProgress(50), 200);
      const timer2 = setTimeout(() => setProgress(80), 400);

      return () => {
        clearTimeout(timer0);
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
    // Complete progress
    if (progress > 0) {
      const timerDone = setTimeout(() => setProgress(100), 0);
      const timer = setTimeout(() => setProgress(0), 300);
      return () => {
        clearTimeout(timerDone);
        clearTimeout(timer);
      };
    }
  }, [navigation.state, progress]);

  if (progress === 0) {
    return null;
  }

  return (
    <>
      {/* Loading bar */}
      <div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#283673] via-[#3a4a8f] to-[#283673] z-[9999] transition-all duration-300 ease-out shadow-lg"
        style={{
          width: `${progress}%`,
          opacity: progress === 100 ? 0 : 1,
        }}
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Page loading progress"
      />

      {/* Pulsing effect at the end */}
      {progress < 100 && (
        <div
          className="fixed top-0 h-1 w-20 bg-[#4a5a9f] z-[10000] animate-pulse blur-sm"
          style={{
            left: `${progress}%`,
            transform: 'translateX(-50%)',
          }}
        />
      )}

      {/* Screen reader announcement */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {navigation.state === 'loading' && 'Loading page...'}
        {navigation.state === 'idle' && progress > 0 && 'Page loaded'}
      </div>
    </>
  );
}

export const GlobalLoadingBar = memo(GlobalLoadingBarComponent);
